import { derived, type Readable, type Writable, writable } from 'svelte/store';

import { setCookie } from '@components/utils/cookies';
import { USER_TIMEOUT_MS } from '@shared/constants';
import { Cookie } from '@shared/cookies';
import { Orientation } from '@shared/types';
import { lockBodyScroll, unlockBodyScroll } from '@utils/body-scroll';

import { type GlobalStore, type GlobalStoreState, type InternetStateStoreInitials } from './types';

const initialValues: GlobalStoreState = {
    isDesktopSite: false,
    isCookiesAllowed: false,
    isShowFooter: true,
    isShowHeader: true,
    isKeyboardVisible: false,
    bodyClassesMap: new Map(),
    orientation: Orientation.Horizontal,
    geoBlocked: null,
};

function createStore(): GlobalStore {
    const store = writable(initialValues);
    const { subscribe, update } = store;

    const updateStore = (data: Partial<GlobalStoreState>) => {
        update((state) => {
            return {
                ...state,
                ...data,
            };
        });
    };

    return {
        subscribe,
        init: (serverInitials) => {
            updateStore({
                ...initialValues,
                ...serverInitials,
            });
        },
        switchToDesktop: (isDesktop, shouldRemember = true) => {
            updateStore({ isDesktopSite: isDesktop });
            if (shouldRemember) {
                setCookie(Cookie.IsDesktopSite, `${isDesktop}`);
            }
        },
        allowCookies: () => {
            updateStore({ isCookiesAllowed: true });
            setCookie(Cookie.CookiesAllowedAt, `${(new Date()).toISOString()}`);
        },
        toggleFooterVisibility: (isShowFooter) => {
            updateStore({ isShowFooter });
        },
        toggleHeaderVisibility: (isShowHeader) => {
            updateStore({ isShowHeader });
        },
        toggleKeyboardVisibility: (isKeyboardVisible: boolean) => {
            isKeyboardVisible ? lockBodyScroll('global') : unlockBodyScroll('global');
            update((state: GlobalStoreState) => ({ ...state, isKeyboardVisible }));
        },
        addBodyClass: (value) => {
            update((state) => {
                state.bodyClassesMap.set(value, value);
                return state;
            });
        },
        deleteBodyClass: (value) => {
            update((state) => {
                state.bodyClassesMap.delete(value);
                return state;
            });
        },
        setOrientation: (orientation) => updateStore({ orientation }),
        setGeoBlocked: (geoBlocked: string | null) => updateStore({ geoBlocked }),
    };
}

export const globalStore = createStore();

export const bodyClass = derived(globalStore, (store) => Array.from(store.bodyClassesMap.keys()).join(' '));

export const isInternetAvailable: Writable<boolean> = writable(true);

export const prevPagePath: Writable<string | undefined> = writable(undefined);

export const internetStateStore: Readable<InternetStateStoreInitials> = derived(
    [isInternetAvailable],
    ([$isInternetAvailable], set) => {
        let internetConnectionTimeout: number | undefined = undefined;
        let isReconnecting = false;
        let isNoInternet = false;

        if ($isInternetAvailable) {
            if (internetConnectionTimeout) {
                clearTimeout(internetConnectionTimeout);
                internetConnectionTimeout = undefined;
            }
            isReconnecting = false;
            isNoInternet = false;
            set({ isReconnecting, isNoInternet });
        } else {
            if (!internetConnectionTimeout) {
                isReconnecting = true;
                isNoInternet = false;
                set({ isReconnecting, isNoInternet });

                internetConnectionTimeout = window.setTimeout(() => {
                    internetConnectionTimeout = undefined;
                    isReconnecting = false;
                    isNoInternet = true;
                    set({ isReconnecting, isNoInternet });
                }, USER_TIMEOUT_MS);
            }
        }

        return () => {
            clearTimeout(internetConnectionTimeout);
            internetConnectionTimeout = undefined;
        };
    }
);
